import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, ArrowRight } from "lucide-react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import useSEO from "../hooks/useSEO";
import { readingTime, SITE_ORIGIN } from "../config/blogPosts";

function slugify(text) {
  return String(text)
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .slice(0, 80);
}

export default function BlogLayout({
  post,
  breadcrumbListTitle,
  faqs,
  ctaHeading,
  ctaButtonLabel,
  children,
}) {
  const articleRef = useRef(null);
  const [toc, setToc] = useState([]);
  const [activeId, setActiveId] = useState("");

  const canonical = `${SITE_ORIGIN}/blog/${post.slug}`;
  const readMin = readingTime(post.wordCount);

  // Build JSON-LD (Article + BreadcrumbList + FAQPage)
  const jsonLd = useMemo(() => {
    const blocks = [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: post.title,
        description: post.metaDescription,
        mainEntityOfPage: canonical,
        datePublished: post.publishDate,
        author: { "@type": "Organization", name: "GetDeparto" },
        publisher: {
          "@type": "Organization",
          name: "GetDeparto",
          url: SITE_ORIGIN || "https://www.getdeparto.com",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_ORIGIN}/` },
          { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE_ORIGIN}/blog` },
          {
            "@type": "ListItem",
            position: 3,
            name: breadcrumbListTitle || post.listTitle || post.title,
            item: canonical,
          },
        ],
      },
    ];
    if (faqs && faqs.length) {
      blocks.push({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      });
    }
    return blocks;
  }, [post, canonical, faqs, breadcrumbListTitle]);

  useSEO({
    title: post.metaTitle,
    description: post.metaDescription,
    canonical,
    jsonLd,
  });

  // Auto-build Table of Contents from H2s inside the article
  useEffect(() => {
    if (!articleRef.current) return;
    const h2s = Array.from(articleRef.current.querySelectorAll("h2"));
    const items = h2s.map((h) => {
      if (!h.id) h.id = slugify(h.textContent || "");
      return { id: h.id, text: h.textContent || "" };
    });
    setToc(items);
  }, [children]);

  // Scroll-spy
  useEffect(() => {
    if (!toc.length) return;
    const handler = () => {
      const y = window.scrollY + 120;
      let current = toc[0]?.id || "";
      toc.forEach((t) => {
        const el = document.getElementById(t.id);
        if (el && el.offsetTop <= y) current = t.id;
      });
      setActiveId(current);
    };
    handler();
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, [toc]);

  const smoothScrollTo = (id) => (e) => {
    const el = document.getElementById(id);
    if (!el) return;
    e.preventDefault();
    const top = el.getBoundingClientRect().top + window.scrollY - 80;
    window.scrollTo({ top, behavior: "smooth" });
    history.replaceState(null, "", `#${id}`);
  };

  return (
    <div style={{ minHeight: "100vh", background: "#fff" }}>
      <Navbar />

      <main className="gd-blog-main">
        <div className="gd-blog-shell">
          {/* Breadcrumb */}
          <nav className="gd-breadcrumb" aria-label="Breadcrumb" data-testid="breadcrumb">
            <Link to="/" className="gd-breadcrumb-link">Home</Link>
            <ChevronRight size={14} className="gd-breadcrumb-sep" aria-hidden />
            <Link to="/blog" className="gd-breadcrumb-link">Blog</Link>
            <ChevronRight size={14} className="gd-breadcrumb-sep" aria-hidden />
            <span className="gd-breadcrumb-current">
              {breadcrumbListTitle || post.listTitle || post.title}
            </span>
          </nav>

          <div className="gd-blog-grid">
            {/* TOC sidebar (desktop only, auto-hidden on narrow screens via CSS) */}
            {toc.length > 1 && (
              <aside className="gd-toc" aria-label="Table of contents" data-testid="blog-toc">
                <div className="gd-toc-title">On this page</div>
                <ul className="gd-toc-list">
                  {toc.map((t) => (
                    <li key={t.id}>
                      <a
                        href={`#${t.id}`}
                        onClick={smoothScrollTo(t.id)}
                        className={`gd-toc-link ${activeId === t.id ? "is-active" : ""}`}
                        data-testid={`toc-link-${t.id}`}
                      >
                        {t.text}
                      </a>
                    </li>
                  ))}
                </ul>
              </aside>
            )}

            {/* Article body */}
            <article
              ref={articleRef}
              className="gd-article"
              data-testid="blog-article"
            >
              <header className="gd-article-header">
                <div className="gd-article-meta" data-testid="article-meta">
                  <span className="gd-article-category">{post.category}</span>
                  <span className="gd-article-dot" aria-hidden>·</span>
                  <span className="gd-article-readtime" data-testid="article-read-time">
                    {readMin} min read
                  </span>
                </div>
                <h1 className="gd-article-title" data-testid="article-title">
                  {post.title}
                </h1>
                <p className="gd-article-excerpt">{post.excerpt}</p>
              </header>

              <div className="gd-article-body">{children}</div>

              {/* End CTA */}
              <aside className="gd-article-cta" data-testid="article-cta">
                <div>
                  <h3 className="gd-article-cta-title">{ctaHeading}</h3>
                  <p className="gd-article-cta-sub">
                    Live PNR · delivered on WhatsApp + email in under 60 minutes.
                  </p>
                </div>
                <Link
                  to="/"
                  className="gd-article-cta-btn"
                  data-testid="article-cta-btn"
                >
                  {ctaButtonLabel} <ArrowRight size={16} />
                </Link>
              </aside>
            </article>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
